import type { DateRange } from '@mui/x-date-pickers-pro/models';
import type { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import type { ComponentProps } from 'react';
import type { DatePresetKey } from '#utils/dates.ts';

import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router';

import { datePresets, FORMATS, getDefaultDateRange } from '#utils/dates.ts';
import { useFilters } from './FiltersContext.tsx';

const resetLabel = 'Reset';

const labelToPresetKey = new Map<string, DatePresetKey | 'reset'>([
  ...Object.entries(datePresets).map(
    ([key, preset]) => [preset.label, key] as [string, DatePresetKey]
  ),
  [resetLabel, 'reset' as const],
]);

const shortcuts: PickersShortcutsItem<DateRange<Date>>[] = [
  ...Object.entries(datePresets).map(([, preset]) => ({
    label: preset.label,
    getValue: preset.getValue,
  })),
  { label: resetLabel, getValue: getDefaultDateRange },
];

export function DateRange(props: Pick<ComponentProps<typeof DateRangePicker>, 'defaultValue'>) {
  const [searchParams] = useSearchParams();

  const filters = useFilters();

  return (
    <DateRangePicker
      localeText={{ cancelButtonLabel: 'Dismiss', okButtonLabel: 'Save' }}
      closeOnSelect={false}
      disableFuture
      defaultValue={props.defaultValue}
      label="Date Range"
      onAccept={([from, to], context) => {
        if (!from || !to) return;
        const preset = context.shortcut ? labelToPresetKey.get(context.shortcut.label) : undefined;

        if (!preset && !searchParams.has('dp') && props.defaultValue) {
          const [defaultFrom, defaultTo] = props.defaultValue;
          const isUnchanged =
            defaultFrom &&
            defaultTo &&
            format(from, FORMATS.MYSQL_DATE) === format(defaultFrom, FORMATS.MYSQL_DATE) &&
            format(to, FORMATS.MYSQL_DATE) === format(defaultTo, FORMATS.MYSQL_DATE);
          if (isUnchanged) return;
        }

        filters.onChange(
          preset
            ? preset === 'reset'
              ? { datePreset: undefined, from: undefined, to: undefined }
              : { datePreset: preset, from: undefined, to: undefined }
            : {
                datePreset: undefined,
                from: format(from, FORMATS.MYSQL_DATE),
                to: format(to, FORMATS.MYSQL_DATE),
              }
        );
      }}
      slotProps={{
        actionBar: {
          actions: ['cancel', 'accept'],
        },
        shortcuts: {
          items: shortcuts,
        },
        textField: {
          fullWidth: true,
        },
      }}
    />
  );
}
